import {
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { DeploymentDetail } from 'deployments/views/DeploymentDetail/DeploymentDetail';
import { EditDeploymentForm } from 'deployments/components/Form/EditForm';
import { SplitView } from 'components/SplitView/SplitView';
import { useCamera } from 'cameras/hooks/useCamera';
import { useDeployment } from 'deployments/hooks/useDeployment';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';

export type DetailRouteWithDeploymentRefProps = {
  children: React.ReactNode;
};

function EditDeploymentDrawer() {
  const { deploymentID, cameraID } = useParams<{
    deploymentID: string;
    cameraID?: string;
  }>();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { data: deployment, isLoading: isLoadingDeployment } =
    useDeployment(deploymentID);
  const { data: camera, isLoading: isLoadingCamera } = useCamera(cameraID, {
    enabled: Boolean(cameraID),
  });
  const { isRingPlan } = useCurrentPlan();

  function handleClose(isOpen?: boolean) {
    if (!isOpen) {
      navigate({ pathname: '..', search });
    }
  }

  // Check if this deployment is a reference deployment for this camera
  const isReferenceDeployment =
    Boolean(cameraID) &&
    Boolean(camera) &&
    Boolean(deploymentID) &&
    camera?.reference_deployment_ids?.includes(deploymentID || '');

  // Only disable start on deploy for reference deployments on Ring plans
  const canStartOnDeploy = !(isReferenceDeployment && isRingPlan);

  if (isLoadingDeployment || (cameraID && isLoadingCamera) || !deployment) {
    return null;
  }

  return (
    <SplitView onOpenChange={handleClose} easeOut isStacked open>
      <EditDeploymentForm
        deployment={deployment}
        startInEditMode
        onSuccess={() => handleClose(false)}
        canStartOnDeploy={canStartOnDeploy}
        allowedCameraIds={isReferenceDeployment && cameraID ? [cameraID] : undefined}
      />
    </SplitView>
  );
}

export function DetailRouteWithDeploymentRef({
  children,
}: DetailRouteWithDeploymentRefProps) {
  const { search } = useLocation();
  const navigate = useNavigate();

  function closeDetail(isOpen?: boolean) {
    if (!isOpen) {
      navigate({ pathname: '..', search });
    }
  }

  function closeDeploymentDetail(isOpen?: boolean) {
    if (!isOpen) {
      navigate({ pathname: '.', search });
    }
  }

  return (
    <Routes>
      <Route
        path="*"
        element={
          <>
            <SplitView onOpenChange={closeDetail} easeOut open>
              {children}
            </SplitView>
            <Outlet />
          </>
        }
      >
        <Route
          path="ref/:deploymentID"
          element={
            <SplitView
              onOpenChange={closeDeploymentDetail}
              easeOut
              isStacked
              open
            >
              <DeploymentDetail />
            </SplitView>
          }
        />
        <Route path="edit/:deploymentID" element={<EditDeploymentDrawer />} />
      </Route>
    </Routes>
  );
}
